import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, CheckPicker, DateRangePicker, Message, Placeholder } from 'rsuite';
import { FilterField, FilterToolbar, renderSelectAllFooter } from '@nsi-advisory-services/components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { showToast } from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import ApprovalRequestCard from '../components/approvals/ApprovalRequestCard';
import ApprovalGroupCard from '../components/approvals/ApprovalGroupCard';
import { employeeName } from '../components/approvals/approvalFormat';
import ChoiceWheel from '../components/shared/ChoiceWheel';
import PageHeader from '../components/shared/PageHeader';
import { getCategoryLabel } from '../utils/expenseCategories';
import { getQuickDateRanges } from '../utils/dateRanges';
import { toDateOnlyString, toStartOfDay } from '../utils/payPeriods';
import { buildExpenseGroupBlocks } from '../utils/expenseGroups';
import ApprovalReviewModal from '../components/timeoff/ApprovalReviewModal';
import ExpenseMarkPaidModal from '../components/expenses/ExpenseMarkPaidModal';
import ExpenseReceiptModal from '../components/expenses/ExpenseReceiptModal';
import {
  useApproveExpense,
  useApproveTimeOffRequest,
  useCommentExpense,
  useCommentTimeOffRequest,
  useExpense,
  useMarkExpensePaid,
  useRejectExpense,
  useRejectTimeOffRequest,
} from '../hooks/useQueries';
import usePendingApprovals, { countExpenseRequests, isPendingRecord as isPending } from '../hooks/usePendingApprovals';
import './ApprovalsInboxPage.css';
import { renderEmployeeSelection } from '../components/shared/employeeSelection';

const CHANNEL_EXPENSE = 'expense';
const CHANNEL_TIMEOFF = 'timeoff';

const STALE_APPROVAL_TOAST = 'This request has been modified. The queue has been refreshed.';

// How long a decided card stays on screen playing its exit. Short enough not to
// hold up the next decision, long enough to see which card left.
const CLEAR_ANIMATION_MS = 420;

const showApprovalToast = (type, content) => showToast(type, content, { duration: 5000 });

// The server refuses to decide anything that is no longer pending; either
// message means someone else got there first.
const isStaleDecisionError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return (
    message.includes('only pending requests can be')
    || message.includes('only pending expenses can be')
    || message.includes('request not found')
    || message.includes('expense not found')
  );
};

const STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
];

const DEFAULT_STATUS_FILTER = ['pending'];

const isDefaultStatusFilter = (value) => value.length === 1 && value[0] === 'pending';

// The filters live in the URL too, so a filtered view can be sent as a link:
// ?view=expense&status=approved,paid&employee=<id>,<id>&category=<key>&from=2026-10-01&to=2026-10-31
// An empty status list ("everything") is written as status=all.
const listParam = (params, key) => (params.get(key) || '').split(',').map((v) => v.trim()).filter(Boolean);

const dateParam = (params, key) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(params.get(key) || '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

const statusFromParams = (params) => {
  if (!params.has('status')) return DEFAULT_STATUS_FILTER;
  const list = listParam(params, 'status');
  return list.length === 1 && list[0] === 'all' ? [] : list;
};

const dateRangeFromParams = (params) => {
  const from = dateParam(params, 'from');
  const to = dateParam(params, 'to');
  return from && to ? [from, to] : null;
};

// Pending first, then rejected, then approved — the same order the rows inside a
// group card take. Oldest first within a status: the longest wait is the one
// someone is chasing, and a queue that only grows at the bottom keeps its order
// as cards leave.
const STATUS_ORDER = { pending: 0, rejected: 1, approved: 2 };

const byQueueOrder = (a, b) => (
  (STATUS_ORDER[String(a?.status || '').toLowerCase()] ?? 3)
  - (STATUS_ORDER[String(b?.status || '').toLowerCase()] ?? 3)
  || new Date(a?.created_at || 0) - new Date(b?.created_at || 0)
);

/**
 * Approvals as a queue to be emptied rather than a table to be read.
 *
 * Two decisions shape the page. First, expenses and time off are separate
 * queues chosen up front instead of two tables stacked on one screen: they are
 * judged on different things, and mixing them means neither is ever finished.
 * Second, only pending records are ever rendered — a decided one leaves the
 * page — so an empty screen is the signal that the work is done.
 *
 * Filters narrow that queue but are never allowed to counterfeit the signal: the
 * count says "4 of 19" while any are active, and a queue emptied by a filter
 * gets its own panel saying what is still waiting behind it, never "all clear".
 */
export default function ApprovalsInboxPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const {
    canApproveExpenses,
    canViewExpenses,
    canApproveTimeOff,
    expensesQuery,
    timeOffQuery,
    reviewableExpenses: unsortedExpenses,
    reviewableTimeOff: unsortedTimeOff,
  } = usePendingApprovals();

  const canViewPage = canViewExpenses || canApproveTimeOff;
  const hasBothChannels = canViewExpenses && canApproveTimeOff;

  const approveExpenseMutation = useApproveExpense();
  const rejectExpenseMutation = useRejectExpense();
  const commentExpenseMutation = useCommentExpense();
  const approveTimeOffMutation = useApproveTimeOffRequest();
  const rejectTimeOffMutation = useRejectTimeOffRequest();
  const commentTimeOffMutation = useCommentTimeOffRequest();
  const markExpensePaidMutation = useMarkExpensePaid();

  const canManagePayroll = auth.isFinance() || auth.isAdmin() || auth.hasPermission('manage_payroll');

  const [searchParams, setSearchParams] = useSearchParams();

  // Null means "still choosing". Someone with one permission never chooses:
  // the split screen would be a menu of one.
  const [channel, setChannel] = useState(() => {
    if (canViewExpenses && !canApproveTimeOff) return CHANNEL_EXPENSE;
    if (canApproveTimeOff && !canViewExpenses) return CHANNEL_TIMEOFF;
    const view = searchParams.get('view');
    return view === CHANNEL_EXPENSE || view === CHANNEL_TIMEOFF ? view : null;
  });

  const [activeRecord, setActiveRecord] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  // The expense the payment-date dialog is asking about, held apart from
  // activeRecord so the details it was opened from stay put behind it.
  const [paidExpense, setPaidExpense] = useState(null);
  const [paidOnDate, setPaidOnDate] = useState(null);

  // ?expense=<id> opens that expense directly, whatever the filters are set to.
  //
  // Fetched by id rather than looked up in the list: the link is followed from
  // elsewhere in the app - the payroll sheet's reimbursement breakdown - and by
  // then the expense is approved and paid, so the inbox's default pending-only
  // filter would not be holding it. Loading the one record sidesteps the
  // filters entirely instead of quietly rewriting them.
  const deepLinkId = searchParams.get('expense');
  const deepLinkQuery = useExpense(deepLinkId, { enabled: Boolean(deepLinkId) });
  const deepLinkOpened = useRef(false);

  useEffect(() => {
    if (!deepLinkId || deepLinkOpened.current) return;
    const record = deepLinkQuery.data;
    if (!record) return;

    // Once only: closing the dialog must not immediately reopen it, and the
    // URL still carries the parameter afterwards.
    deepLinkOpened.current = true;
    setChannel(CHANNEL_EXPENSE);
    setActiveRecord(record);
    setReviewOpen(true);
  }, [deepLinkId, deepLinkQuery.data]);

  useEffect(() => {
    if (!deepLinkId || !deepLinkQuery.isError) return;
    showToast('error', deepLinkQuery.error?.message || 'That expense could not be opened.');
  }, [deepLinkId, deepLinkQuery.isError, deepLinkQuery.error]);
  const [receiptOpen, setReceiptOpen] = useState(false);
  // Ids mid-exit: decided, but still drawn until the animation finishes.
  const [clearingIds, setClearingIds] = useState([]);
  const clearTimers = useRef([]);

  useEffect(() => () => clearTimers.current.forEach((timer) => window.clearTimeout(timer)), []);

  // Everything the page will ever draw, in the order the queue works through
  // it. Which records are reviewable at all is the hook's business; the sort is
  // this page's, since only the queue reads them in order.
  const reviewableExpenses = useMemo(
    () => [...unsortedExpenses].sort(byQueueOrder),
    [unsortedExpenses]
  );

  const reviewableTimeOff = useMemo(
    () => [...unsortedTimeOff].sort(byQueueOrder),
    [unsortedTimeOff]
  );

  // What is actually waiting on someone, which is what the wheel counts and what
  // the empty states talk about — never the filtered total, which can include
  // records that were decided weeks ago.
  const pendingExpenses = useMemo(() => reviewableExpenses.filter(isPending), [reviewableExpenses]);
  const pendingTimeOff = useMemo(() => reviewableTimeOff.filter(isPending), [reviewableTimeOff]);

  // Pending is the default because it is the work; approved and rejected are
  // there to look something up, not to be worked through.
  const [statusFilter, setStatusFilter] = useState(() => statusFromParams(searchParams));
  const [employeeFilter, setEmployeeFilter] = useState(() => listParam(searchParams, 'employee'));
  const [categoryFilter, setCategoryFilter] = useState(() => listParam(searchParams, 'category'));
  const [dateRange, setDateRange] = useState(() => dateRangeFromParams(searchParams));

  const quickDateRanges = useMemo(() => getQuickDateRanges(), []);

  // The two queues share nothing — a category means nothing to a time off
  // request — so switching wipes the filters rather than carrying over a set
  // that would hide everything in the queue being opened. Not on the way in,
  // though: filters that came in on a link stay.
  const filtersChannel = useRef(channel);
  useEffect(() => {
    if (filtersChannel.current === channel) return;
    filtersChannel.current = channel;
    setStatusFilter(DEFAULT_STATUS_FILTER);
    setEmployeeFilter([]);
    setCategoryFilter([]);
    setDateRange(null);
  }, [channel]);

  // Keep the URL in step with the filters (replacing, so the back button isn't
  // a trail of every pick).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const put = (key, value) => (value ? next.set(key, value) : next.delete(key));
    put('view', hasBothChannels ? channel : null);
    put('status', isDefaultStatusFilter(statusFilter) ? null : (statusFilter.join(',') || 'all'));
    put('employee', employeeFilter.join(','));
    put('category', channel === CHANNEL_EXPENSE ? categoryFilter.join(',') : null);
    const [from, to] = Array.isArray(dateRange) ? dateRange : [];
    put('from', from && to ? toDateOnlyString(from) : null);
    put('to', from && to ? toDateOnlyString(to) : null);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [channel, hasBothChannels, statusFilter, employeeFilter, categoryFilter, dateRange, searchParams, setSearchParams]);

  // Both queues are filtered on the window they overlap rather than on a single
  // date: a time off request and a multi-month tech reimbursement both span a
  // range, and either counts if any part of it falls inside. Same rule as the
  // tables page, so a filter means the same thing in both places.
  const overlapsRange = useMemo(() => {
    const [rawStart, rawEnd] = Array.isArray(dateRange) ? dateRange : [];
    const rangeStart = toStartOfDay(rawStart);
    const rangeEnd = toStartOfDay(rawEnd);

    return (from, to) => {
      if (!rangeStart || !rangeEnd) return true;
      const start = toStartOfDay(from);
      const end = toStartOfDay(to) || start;
      if (!start || !end) return false;
      return start <= rangeEnd && end >= rangeStart;
    };
  }, [dateRange]);

  const matchesEmployee = useMemo(() => {
    const employees = new Set(employeeFilter.map((id) => String(id)));
    return (row) => employees.size === 0 || employees.has(String(row?.employee_id || ''));
  }, [employeeFilter]);

  // An empty selection means every reviewable status rather than none: a picker
  // cleared to nothing should show the queue, not a blank screen.
  const matchesStatus = useMemo(() => {
    const statuses = new Set(statusFilter.map((value) => String(value).toLowerCase()));
    return (row) => statuses.size === 0 || statuses.has(String(row?.status || '').toLowerCase());
  }, [statusFilter]);

  const filteredExpenses = useMemo(() => {
    const categories = new Set(categoryFilter.map((value) => String(value)));
    return reviewableExpenses.filter((row) => (
      matchesStatus(row)
      && matchesEmployee(row)
      && (categories.size === 0 || categories.has(String(row?.category || '')))
      // Tech reimbursements span many months, so use the purchase range when it
      // is there and fall back to the single purchase date.
      && overlapsRange(
        row.purchase_start_date || row.purchase_date,
        row.purchase_end_date || row.purchase_date,
      )
    ));
  }, [reviewableExpenses, categoryFilter, matchesStatus, matchesEmployee, overlapsRange]);

  const filteredTimeOff = useMemo(() => reviewableTimeOff.filter((row) => (
    matchesStatus(row)
    && matchesEmployee(row)
    && overlapsRange(row.start_date, row.end_date)
  )), [reviewableTimeOff, matchesStatus, matchesEmployee, overlapsRange]);

  const isExpenseChannel = channel === CHANNEL_EXPENSE;
  const records = isExpenseChannel ? filteredExpenses : filteredTimeOff;
  const pendingRecords = isExpenseChannel ? pendingExpenses : pendingTimeOff;
  const showsOnlyPending = isDefaultStatusFilter(statusFilter);
  const hasActiveFilters = !showsOnlyPending
    || employeeFilter.length > 0
    || categoryFilter.length > 0
    || Boolean(dateRange);

  const clearFilters = useCallback(() => {
    setStatusFilter(DEFAULT_STATUS_FILTER);
    setEmployeeFilter([]);
    setCategoryFilter([]);
    setDateRange(null);
  }, []);

  // Options come from everything the status filter can reach, not from what is
  // on screen: options that appeared and vanished as you picked them would make
  // the pickers unusable.
  const employeeOptions = useMemo(() => {
    const byId = new Map();
    (isExpenseChannel ? reviewableExpenses : reviewableTimeOff).forEach((row) => {
      const id = String(row?.employee_id || '');
      if (!id || byId.has(id)) return;
      byId.set(id, { label: employeeName(row), value: id });
    });
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [isExpenseChannel, reviewableExpenses, reviewableTimeOff]);

  const categoryOptions = useMemo(() => {
    const byValue = new Map();
    reviewableExpenses.forEach((row) => {
      const value = String(row?.category || '');
      if (!value || byValue.has(value)) return;
      byValue.set(value, { label: getCategoryLabel(value) || value, value });
    });
    return [...byValue.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [reviewableExpenses]);

  // A submitted batch arrives as one thing, so the expense queue is laid out as
  // groups and loose expenses interleaved on submission order rather than as a
  // flat run of cards.
  //
  // A group appears only while it still has something pending — the queue has to
  // empty — but it then lists the batch in full, decided members included. Their
  // status is the context the pending ones are judged against: three of five
  // already approved says something about the two that are left. That is also
  // why filtering picks which batches appear rather than which rows inside one
  // do: a half-listed batch would total up to the wrong number.
  const expenseBlocks = useMemo(() => {
    const pendingIds = new Set(filteredExpenses.map((row) => String(row.id)));
    const reviewableGroupIds = new Set(
      filteredExpenses.filter((row) => row.group_id).map((row) => String(row.group_id))
    );

    const rows = (expensesQuery.data || []).filter((row) => (
      row?.group_id
        ? reviewableGroupIds.has(String(row.group_id))
        : pendingIds.has(String(row?.id))
    ));

    return buildExpenseGroupBlocks(rows);
  }, [expensesQuery.data, filteredExpenses]);

  // Oldest first in both queues above: the longest wait is the one someone is
  // chasing, and a stack that only ever grows at the bottom keeps its order as
  // cards leave.

  const markCleared = useCallback((id) => {
    const key = String(id);
    setClearingIds((prev) => (prev.includes(key) ? prev : [...prev, key]));
    const timer = window.setTimeout(() => {
      setClearingIds((prev) => prev.filter((value) => value !== key));
    }, CLEAR_ANIMATION_MS);
    clearTimers.current.push(timer);
  }, []);

  const activeQuery = channel === CHANNEL_EXPENSE ? expensesQuery : timeOffQuery;

  const openReview = async (record) => {
    // Already decided, and on screen because the status filter asked for it.
    // There is nothing to be stale about and nothing to decide, so open it as
    // the read-only detail view the modal renders for a decided record.
    if (!isPending(record)) {
      setActiveRecord(record);
      setReviewOpen(true);
      return;
    }

    // Re-read before opening so a decision made elsewhere in the last minute
    // shows up as "already handled" rather than as a failed approve.
    let latest = record;
    try {
      const refreshed = await activeQuery.refetch();
      latest = (refreshed?.data || []).find((row) => String(row?.id) === String(record?.id)) || null;
    } catch (error) {
      showApprovalToast('error', String(error?.message || error || 'Unable to refresh approvals'));
      return;
    }

    if (!latest || !isPending(latest)) {
      showApprovalToast('warning', STALE_APPROVAL_TOAST);
      return;
    }

    setActiveRecord(latest);
    setReviewOpen(true);
  };

  const closeReview = () => {
    setReviewOpen(false);
    setActiveRecord(null);
  };

  // Approve, reject and comment differ only in which mutation they run and what
  // they say afterwards, so they share one path through the stale handling.
  const runDecision = async (mutation, { id, approval_reason, successToast, failureMessage, clears }) => {
    try {
      await mutation.mutateAsync({ id, approval_reason });
      if (successToast) showApprovalToast('success', successToast);
      if (clears) markCleared(id);
      closeReview();
    } catch (error) {
      if (isStaleDecisionError(error)) {
        showApprovalToast('warning', STALE_APPROVAL_TOAST);
        closeReview();
        await activeQuery.refetch();
        return;
      }
      showApprovalToast('error', String(error?.message || error || failureMessage));
    }
  };

  const submitApprove = (approval_reason) => runDecision(
    isExpenseChannel ? approveExpenseMutation : approveTimeOffMutation,
    {
      id: activeRecord?.id,
      approval_reason,
      failureMessage: 'Unable to approve request',
      clears: true,
    },
  );

  const submitReject = (approval_reason) => runDecision(
    isExpenseChannel ? rejectExpenseMutation : rejectTimeOffMutation,
    {
      id: activeRecord?.id,
      approval_reason,
      failureMessage: 'Unable to reject request',
      clears: true,
    },
  );

  // A comment leaves the record pending, so its card stays in the queue.
  const submitComment = (approval_reason) => runDecision(
    isExpenseChannel ? commentExpenseMutation : commentTimeOffMutation,
    {
      id: activeRecord?.id,
      approval_reason,
      successToast: 'Comment saved',
      failureMessage: 'Unable to save comment',
      clears: false,
    },
  );

  // An approved expense reaches this queue through the ?expense= link the
  // payroll sheet follows, so the payment date can be recorded here rather than
  // sending payroll back to the board for it.
  const openMarkPaid = (expense) => {
    if (!expense?.id) return;
    setPaidExpense(expense);
    setPaidOnDate(new Date());
  };

  const closeMarkPaid = () => {
    setPaidExpense(null);
    setPaidOnDate(null);
  };

  const submitMarkPaid = async () => {
    if (!paidExpense?.id || !paidOnDate) return;
    const paidOn = toDateOnlyString(paidOnDate);

    try {
      await markExpensePaidMutation.mutateAsync({ id: paidExpense.id, paid_on: paidOn });
      // The details are still open behind this dialog and would go on
      // describing an unpaid expense otherwise.
      setActiveRecord((prev) => (
        prev && String(prev.id) === String(paidExpense.id) ? { ...prev, paid_on: paidOn } : prev
      ));
      closeMarkPaid();
      showApprovalToast('success', 'Expense marked paid');
    } catch (error) {
      showApprovalToast('error', String(error?.message || error || 'Unable to mark this expense paid'));
      await expensesQuery.refetch();
    }
  };

  const handlePrintReceipt = () => {
    if (!activeRecord?.id) return;
    setReviewOpen(false);
    setReceiptOpen(true);
  };

  const handleReceiptCancel = () => {
    setReceiptOpen(false);
    if (activeRecord?.id) setReviewOpen(true);
  };

  if (auth.isLoading || auth.isResolving) {
    return <Placeholder.Paragraph rows={6} active />;
  }

  if (!canViewPage) {
    return (
      <Message type="error" header="Access denied" showIcon>
        You do not have permission to view approvals.
      </Message>
    );
  }

  const loading = (canViewExpenses && expensesQuery.isLoading)
    || (canApproveTimeOff && timeOffQuery.isLoading);

  if (loading) {
    return <Placeholder.Paragraph rows={6} active />;
  }

  if (expensesQuery.error || timeOffQuery.error) {
    return (
      <Message type="error" header="Error" showIcon>
        {String(
          expensesQuery.error?.message
          || timeOffQuery.error?.message
          || expensesQuery.error
          || timeOffQuery.error,
        )}
      </Message>
    );
  }

  // ---- Which queue are we working? ----

  if (!channel) {
    return (
      <div className="approvals-inbox">
        {/* The same wheel the other landing pages use, so Approvals is picked
            the way Admin and Time Off are. The tiles carry the count of what is
            waiting behind each, which is the one thing that decides where to
            start. */}
        <ChoiceWheel
          title="Approvals"
          reservedHeight={110}
          items={[
            {
              key: CHANNEL_EXPENSE,
              icon: 'bi-receipt',
              title: 'Expense Requests',
              description: 'Receipts, vendors and amounts awaiting a decision.',
              badge: countExpenseRequests(pendingExpenses),
              onClick: () => setChannel(CHANNEL_EXPENSE),
            },
            {
              key: CHANNEL_TIMEOFF,
              icon: 'bi-calendar2-week',
              title: 'Time Off Requests',
              description: 'Vacation, sick and reduction requests awaiting a decision.',
              badge: pendingTimeOff.length,
              onClick: () => setChannel(CHANNEL_TIMEOFF),
            },
          ]}
        />

        <div className="approvals-inbox__footnote">
          <Button appearance="link" onClick={() => navigate('/approvals/tables')}>
            Open the table view instead
          </Button>
        </div>
      </div>
    );
  }

  // ---- The queue itself ----

  const title = isExpenseChannel ? 'Expense Requests' : 'Time Off Requests';
  const shown = records.length;
  const waiting = pendingRecords.length;

  // An empty queue means the work is done; an empty *filter* does not, and once
  // the status filter is off pending the number on screen isn't the workload at
  // all. The count says which it is looking at rather than letting the two blur.
  const countLabel = showsOnlyPending
    ? (shown < waiting ? `${shown} of ${waiting} awaiting review` : `${shown} awaiting review`)
    : `${shown} shown · ${waiting} awaiting review`;

  return (
    <div className="approvals-inbox bdc-page">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          ...(hasBothChannels ? [{ label: 'Approvals' }] : []),
          { label: title },
        ]}
        icon={isExpenseChannel ? 'bi-receipt' : 'bi-calendar2-week'}
        title={title}
        note={countLabel}
        // With both queues in play, back is the choice between them rather
        // than a page above this one.
        onBack={hasBothChannels ? () => setChannel(null) : null}
        actions={(
          <>
            {isExpenseChannel ? (
              <button
                type="button"
                className="bdc-btn"
                onClick={() => navigate('/approvals/payroll-view')}
              >
                Payroll View
                <i className="bi bi-cash-stack" aria-hidden="true" />
              </button>
            ) : null}
            <button
              type="button"
              className="bdc-btn"
              onClick={() => navigate('/approvals/tables')}
            >
              Tables
              <i className="bi bi-table" aria-hidden="true" />
            </button>
          </>
        )}
      />

      {/* No point offering to filter a channel with nothing in it at all — and
          the toolbar would sit above the "all clear" panel arguing with it. The
          test is everything reviewable, not what is pending: a worked-out queue
          can still have decided records worth looking up. */}
      {(isExpenseChannel ? reviewableExpenses : reviewableTimeOff).length > 0 ? (
        <FilterToolbar>
          <FilterField label="Employees" basis={240} max={340}>
            <CheckPicker
              data={employeeOptions}
              countable={false}
              renderValue={renderEmployeeSelection(employeeOptions)}
              value={employeeFilter}
              onChange={(value) => setEmployeeFilter(value || [])}
              placeholder="All Employees"
              searchable
              preventOverflow
              renderExtraFooter={renderSelectAllFooter(employeeOptions, employeeFilter, setEmployeeFilter)}
              block
            />
          </FilterField>

          {isExpenseChannel ? (
            <FilterField label="Categories" basis={220} max={320}>
              <CheckPicker
                data={categoryOptions}
                value={categoryFilter}
                onChange={(value) => setCategoryFilter(value || [])}
                placeholder="All Categories"
                countable
                searchable
                preventOverflow
                renderExtraFooter={renderSelectAllFooter(categoryOptions, categoryFilter, setCategoryFilter)}
                block
              />
            </FilterField>
          ) : null}

          <FilterField label="Statuses" basis={180} max={260}>
            <CheckPicker
              data={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value || [])}
              placeholder="All Statuses"
              countable
              searchable={false}
              preventOverflow
              renderExtraFooter={renderSelectAllFooter(STATUS_OPTIONS, statusFilter, setStatusFilter)}
              block
            />
          </FilterField>

          <FilterField
            label={isExpenseChannel ? 'Purchase Dates' : 'Time Off Dates'}
            basis={230}
            max={320}
          >
            <DateRangePicker
              value={dateRange}
              onChange={(value) => setDateRange(value || null)}
              format="MM/dd/yyyy"
              character=" – "
              placeholder="All Dates"
              isoWeek={false}
              weekStart={0}
              ranges={quickDateRanges}
              preventOverflow
              block
            />
          </FilterField>

          {hasActiveFilters ? (
            <FilterField>
              <Button appearance="ghost" onClick={clearFilters}>
                Clear filters
              </Button>
            </FilterField>
          ) : null}
        </FilterToolbar>
      ) : null}

      {shown === 0 ? (
        hasActiveFilters ? (
          <div className="approvals-inbox__done approvals-inbox__done--filtered">
            <i className="bi bi-funnel" aria-hidden="true" />
            <h3>Nothing matches these filters</h3>
            <p>
              {waiting === 0
                ? `Nothing is waiting in ${title.toLowerCase()} either, so this is only hiding decided requests.`
                : `${waiting} request${waiting === 1 ? ' is' : 's are'} still waiting in ${title.toLowerCase()}, hidden by the filters above.`}
            </p>
            <Button appearance="primary" onClick={clearFilters}>
              Clear filters
            </Button>
          </div>
        ) : (
          <div className="approvals-inbox__done">
            <i className="bi bi-check2-circle" aria-hidden="true" />
            <h3>All clear</h3>
            <p>
              Nothing is waiting on you in {title.toLowerCase()}.
            </p>
            {hasBothChannels ? (
              <Button
                appearance="primary"
                onClick={() => setChannel(isExpenseChannel ? CHANNEL_TIMEOFF : CHANNEL_EXPENSE)}
              >
                Go to {isExpenseChannel ? 'Time Off' : 'Expense'} Requests
              </Button>
            ) : null}
          </div>
        )
      ) : (
        <div className="approvals-inbox__grid">
          {isExpenseChannel ? expenseBlocks.map((block) => (
            block.type === 'group' ? (
              <ApprovalGroupCard
                key={block.key}
                group={block.group}
                clearingIds={clearingIds}
                onReview={openReview}
              />
            ) : (
              <ApprovalRequestCard
                key={block.key}
                kind={CHANNEL_EXPENSE}
                record={block.expense}
                clearing={clearingIds.includes(String(block.expense.id))}
                onReview={openReview}
              />
            )
          )) : records.map((record) => (
            <ApprovalRequestCard
              key={record.id}
              kind={channel}
              record={record}
              clearing={clearingIds.includes(String(record.id))}
              onReview={openReview}
            />
          ))}
        </div>
      )}

      <ApprovalReviewModal
        open={reviewOpen}
        onClose={closeReview}
        record={activeRecord}
        recordType={isExpenseChannel ? 'expense' : 'timeoff'}
        onApprove={submitApprove}
        onReject={submitReject}
        onSaveComment={submitComment}
        // View All Expenses without Approve Expenses: looks, never decides.
        canDecide={isExpenseChannel ? canApproveExpenses : true}
        onPrintReceipt={isExpenseChannel ? handlePrintReceipt : undefined}
        onMarkPaid={isExpenseChannel && canManagePayroll ? openMarkPaid : undefined}
        loading={
          approveExpenseMutation.isPending
          || rejectExpenseMutation.isPending
          || approveTimeOffMutation.isPending
          || rejectTimeOffMutation.isPending
        }
        commentLoading={commentExpenseMutation.isPending || commentTimeOffMutation.isPending}
        markPaidLoading={markExpensePaidMutation.isPending}
      />

      <ExpenseMarkPaidModal
        open={Boolean(paidExpense)}
        value={paidOnDate}
        onChange={setPaidOnDate}
        onSubmit={submitMarkPaid}
        onClose={closeMarkPaid}
        loading={markExpensePaidMutation.isPending}
      />

      {isExpenseChannel ? (
        <ExpenseReceiptModal
          open={receiptOpen}
          expense={activeRecord}
          onCancel={handleReceiptCancel}
          onClose={() => setReceiptOpen(false)}
        />
      ) : null}
    </div>
  );
}
